import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, User } from 'lucide-react';
import { Database } from '../../../lib/database.types';
import toast from 'react-hot-toast';

type Customer = Database['public']['Tables']['customers']['Row'];

interface CustomerFormModalProps {
  isOpen: boolean;
  customer?: Customer;
  onSubmit: (data: Partial<Customer>) => Promise<void>;
  onCancel: () => void;
  busy?: boolean;
}

export function CustomerFormModal({
  isOpen,
  customer,
  onSubmit,
  onCancel,
  busy = false
}: CustomerFormModalProps) {
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    phone: customer?.phone || '',
    email: customer?.email || ''
  });

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '',
        email: customer.email || ''
      });
    } else {
      setFormData({
        name: '',
        phone: '',
        email: ''
      });
    }
  }, [customer, isOpen]);

  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
      window.dispatchEvent(new CustomEvent('ui:customer-modal', { detail: { open: true } }));
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
      window.dispatchEvent(new CustomEvent('ui:customer-modal', { detail: { open: false } }));
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
      window.dispatchEvent(new CustomEvent('ui:customer-modal', { detail: { open: false } }));
    };
  }, [isOpen]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!/^05\d{8}$/.test(formData.phone)) {
      toast.error('מספר טלפון לא תקין - נדרש מספר בפורמט 05XXXXXXXX');
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('כתובת אימייל לא תקינה');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('שם הלקוח הוא שדה חובה');
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting customer:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          onClick={onCancel}
          style={{ touchAction: 'none' }}
        />
        <div className="absolute inset-0 flex items-end sm:items-center justify-center pointer-events-none">
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto"
            style={{ height: '85vh', maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* iOS Style Header - Sticky with Blur */}
            <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
              <div className="flex items-center justify-between px-5 py-4">
                <button
                  onClick={onCancel}
                  className="text-blue-600 font-semibold text-[17px] active:opacity-50 transition-opacity"
                  disabled={busy}
                >
                  ביטול
                </button>
                <h2 className="text-[17px] font-semibold text-gray-900">
                  {customer ? 'עריכת לקוח' : 'לקוח חדש'}
                </h2>
                <button
                  onClick={() => handleSubmit()}
                  disabled={busy}
                  className="text-blue-600 font-semibold text-[17px] active:opacity-50 transition-opacity disabled:opacity-30"
                >
                  {busy ? 'שומר...' : 'שמור'}
                </button>
              </div>
            </div>

            {/* Content */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-5 space-y-6">
                {/* Profile Avatar - iOS Style */}
                <div className="flex flex-col items-center py-6">
                  <div className="h-32 w-32 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg">
                    <User className="h-16 w-16 text-white" />
                  </div>
                </div>

                {/* iOS Style Form Groups */}
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-200">
                  {/* Name */}
                  <div className="flex items-center px-4 py-3 border-b border-gray-100">
                    <label className="text-gray-900 font-medium w-24 text-[15px]">שם מלא</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="flex-1 text-[15px] bg-transparent border-none outline-none text-right"
                      placeholder="שם מלא"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div className="flex items-center px-4 py-3 border-b border-gray-100">
                    <Phone className="h-5 w-5 text-gray-400 ml-3" />
                    <label className="text-gray-900 font-medium w-20 text-[15px]">טלפון</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="flex-1 text-[15px] bg-transparent border-none outline-none text-right"
                      placeholder="0501234567"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="flex items-center px-4 py-3">
                    <Mail className="h-5 w-5 text-gray-400 ml-3" />
                    <label className="text-gray-900 font-medium w-20 text-[15px]">אימייל</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="flex-1 text-[15px] bg-transparent border-none outline-none text-right"
                      placeholder="example@email.com"
                    />
                  </div>
                </div>

                {/* Info Card */}
                <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
                  <p className="text-[13px] text-blue-600 font-medium">
                    מספר טלפון חייב להיות בפורמט: 05XXXXXXXX
                  </p>
                  <p className="text-[12px] text-blue-500 mt-1">
                    כתובת אימייל היא שדה אופציונלי
                  </p>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}
